import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { payrollApi } from '../../api/payrollApi';
import {
  PayrollSettingDto,
  SavePayrollSetting,
  SalaryComponentDto,
  CreateSalaryComponent,
  UpdateSalaryComponent,
  SalaryStructureDto,
  SaveSalaryStructure,
  PayrollRunDto,
  PayrollRunDetailDto,
  PayrollRunStatus,
  TaxDeclarationDto,
  SaveTaxDeclaration,
  TaxDeclarationStatus,
  PayslipDto,
  PayrollDashboardDto,
} from '../../types/payrollTypes';

interface PayrollState {
  dashboard: PayrollDashboardDto | null;
  settings: PayrollSettingDto | null;
  salaryComponents: SalaryComponentDto[];
  currentStructure: SalaryStructureDto | null;
  payrollRuns: PayrollRunDto[];
  currentRunDetail: PayrollRunDetailDto | null;
  taxDeclarations: TaxDeclarationDto[];
  payslips: PayslipDto[];
  loading: boolean;
  error: string | null;
}

const initialState: PayrollState = {
  dashboard: null,
  settings: null,
  salaryComponents: [],
  currentStructure: null,
  payrollRuns: [],
  currentRunDetail: null,
  taxDeclarations: [],
  payslips: [],
  loading: false,
  error: null,
};

// ─── Dashboard ────────────────────────────────────────────

export const fetchPayrollDashboard = createAsyncThunk(
  'payroll/fetchDashboard',
  async (params: { month?: number; year?: number } | undefined, { rejectWithValue }) => {
    try {
      return await payrollApi.getDashboard(params?.month, params?.year);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payroll dashboard');
    }
  }
);

// ─── Settings ─────────────────────────────────────────────

export const fetchPayrollSettings = createAsyncThunk(
  'payroll/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      return await payrollApi.getSettings();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payroll settings');
    }
  }
);

export const savePayrollSettings = createAsyncThunk(
  'payroll/saveSettings',
  async (data: SavePayrollSetting, { rejectWithValue }) => {
    try {
      return await payrollApi.saveSettings(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save payroll settings');
    }
  }
);

// ─── Salary Components ────────────────────────────────────

export const fetchSalaryComponents = createAsyncThunk(
  'payroll/fetchSalaryComponents',
  async (activeOnly: boolean | undefined, { rejectWithValue }) => {
    try {
      return await payrollApi.getSalaryComponents(activeOnly);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch salary components');
    }
  }
);

export const createSalaryComponent = createAsyncThunk(
  'payroll/createSalaryComponent',
  async (data: CreateSalaryComponent, { rejectWithValue }) => {
    try {
      return await payrollApi.createSalaryComponent(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create salary component');
    }
  }
);

export const updateSalaryComponent = createAsyncThunk(
  'payroll/updateSalaryComponent',
  async ({ id, data }: { id: string; data: UpdateSalaryComponent }, { rejectWithValue }) => {
    try {
      await payrollApi.updateSalaryComponent(id, data);
      return { id, data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update salary component');
    }
  }
);

// ─── Salary Structure ─────────────────────────────────────

export const fetchSalaryStructure = createAsyncThunk(
  'payroll/fetchSalaryStructure',
  async (employeeId: string, { rejectWithValue }) => {
    try {
      return await payrollApi.getSalaryStructure(employeeId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch salary structure');
    }
  }
);

export const saveSalaryStructure = createAsyncThunk(
  'payroll/saveSalaryStructure',
  async (data: SaveSalaryStructure, { rejectWithValue }) => {
    try {
      return await payrollApi.saveSalaryStructure(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save salary structure');
    }
  }
);

// ─── Payroll Runs ─────────────────────────────────────────

export const fetchPayrollRuns = createAsyncThunk(
  'payroll/fetchRuns',
  async (params: { year?: number; status?: PayrollRunStatus } | undefined, { rejectWithValue }) => {
    try {
      return await payrollApi.getPayrollRuns(params?.year, params?.status);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payroll runs');
    }
  }
);

export const fetchPayrollRunDetail = createAsyncThunk(
  'payroll/fetchRunDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await payrollApi.getPayrollRunDetail(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payroll run detail');
    }
  }
);

export const createPayrollRun = createAsyncThunk(
  'payroll/createRun',
  async ({ month, year }: { month: number; year: number }, { rejectWithValue }) => {
    try {
      return await payrollApi.createPayrollRun(month, year);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create payroll run');
    }
  }
);

export const processPayrollRun = createAsyncThunk(
  'payroll/processRun',
  async (id: string, { rejectWithValue }) => {
    try {
      await payrollApi.processPayrollRun(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to process payroll run');
    }
  }
);

export const lockPayrollRun = createAsyncThunk(
  'payroll/lockRun',
  async (id: string, { rejectWithValue }) => {
    try {
      await payrollApi.lockPayrollRun(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to lock payroll run');
    }
  }
);

// ─── Tax Declarations ─────────────────────────────────────

export const fetchTaxDeclarations = createAsyncThunk(
  'payroll/fetchTaxDeclarations',
  async (params: { employeeId?: string; financialYear?: string } | undefined, { rejectWithValue }) => {
    try {
      return await payrollApi.getTaxDeclarations(params?.employeeId, params?.financialYear);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tax declarations');
    }
  }
);

export const saveTaxDeclaration = createAsyncThunk(
  'payroll/saveTaxDeclaration',
  async (data: SaveTaxDeclaration, { rejectWithValue }) => {
    try {
      return await payrollApi.saveTaxDeclaration(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save tax declaration');
    }
  }
);

export const updateTaxDeclarationStatus = createAsyncThunk(
  'payroll/updateTaxDeclarationStatus',
  async ({ id, status }: { id: string; status: TaxDeclarationStatus }, { rejectWithValue }) => {
    try {
      await payrollApi.updateTaxDeclarationStatus(id, status);
      return { id, status };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update tax declaration status');
    }
  }
);

// ─── Payslips ─────────────────────────────────────────────

export const fetchPayslips = createAsyncThunk(
  'payroll/fetchPayslips',
  async (params: { employeeId?: string; payrollRunId?: string } | undefined, { rejectWithValue }) => {
    try {
      return await payrollApi.getPayslips(params?.employeeId, params?.payrollRunId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payslips');
    }
  }
);

const payrollSlice = createSlice({
  name: 'payroll',
  initialState,
  reducers: {
    clearCurrentRunDetail: (state) => {
      state.currentRunDetail = null;
    },
    clearCurrentStructure: (state) => {
      state.currentStructure = null;
    },
    clearPayrollError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchPayrollDashboard.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPayrollDashboard.fulfilled, (state, action) => { state.loading = false; state.dashboard = action.payload; })
      .addCase(fetchPayrollDashboard.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Settings
      .addCase(fetchPayrollSettings.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPayrollSettings.fulfilled, (state, action) => { state.loading = false; state.settings = action.payload; })
      .addCase(fetchPayrollSettings.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(savePayrollSettings.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(savePayrollSettings.fulfilled, (state) => { state.loading = false; })
      .addCase(savePayrollSettings.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Salary Components
      .addCase(fetchSalaryComponents.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSalaryComponents.fulfilled, (state, action) => { state.loading = false; state.salaryComponents = action.payload; })
      .addCase(fetchSalaryComponents.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createSalaryComponent.pending, (state) => { state.loading = true; })
      .addCase(createSalaryComponent.fulfilled, (state) => { state.loading = false; })
      .addCase(createSalaryComponent.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Salary Structure
      .addCase(fetchSalaryStructure.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSalaryStructure.fulfilled, (state, action) => { state.loading = false; state.currentStructure = action.payload; })
      .addCase(fetchSalaryStructure.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Payroll Runs
      .addCase(fetchPayrollRuns.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPayrollRuns.fulfilled, (state, action) => { state.loading = false; state.payrollRuns = action.payload; })
      .addCase(fetchPayrollRuns.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(fetchPayrollRunDetail.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPayrollRunDetail.fulfilled, (state, action) => { state.loading = false; state.currentRunDetail = action.payload; })
      .addCase(fetchPayrollRunDetail.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createPayrollRun.pending, (state) => { state.loading = true; })
      .addCase(createPayrollRun.fulfilled, (state) => { state.loading = false; })
      .addCase(createPayrollRun.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Tax Declarations
      .addCase(fetchTaxDeclarations.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTaxDeclarations.fulfilled, (state, action) => { state.loading = false; state.taxDeclarations = action.payload; })
      .addCase(fetchTaxDeclarations.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      // Payslips
      .addCase(fetchPayslips.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPayslips.fulfilled, (state, action) => { state.loading = false; state.payslips = action.payload; })
      .addCase(fetchPayslips.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
  },
});

export const { clearCurrentRunDetail, clearCurrentStructure, clearPayrollError } = payrollSlice.actions;
export default payrollSlice.reducer;
